/**
 * The account screen — today, one thing: self-service deletion (§11, Privacy).
 *
 * The sign-in screen promises "you can delete your account and its data at any
 * time", and this is where that promise is kept. It is deliberately the same
 * typed-email confirmation the admin delete uses (admin/Users.tsx): the API
 * route (`DELETE /me`) takes a `confirmEmail` and rejects a mismatch, so the UI
 * mirrors that rather than inventing a softer gate the server would ignore.
 *
 * The email the user must type back is read from `GET /me`, which is the
 * account's authoritative address — not the ID token's self-asserted claim,
 * which the API does not compare against.
 */

import { useState } from 'react';
import { Trash2, TriangleAlert } from 'lucide-react';

import { useDeleteAccount, useMe, useUpdatePreferences } from '../api/hooks';
import { useAuth } from '../auth/AuthProvider';
import { Eyebrow } from '../components/primitives';
import { errorMessage, Loading } from '../components/states';

export function Account() {
  const { isSignedIn } = useAuth();
  const me = useMe(isSignedIn);

  if (me.isPending) return <Loading label="Loading your account…" />;

  // `/me` failing is unusual here (the shell already loaded), but the email is
  // load-bearing for the confirmation, so fall back to a plain error rather than
  // render a delete box that cannot match.
  const user = me.data?.user;
  if (!user) return <Loading />;

  return (
    <div className="screen">
      <Eyebrow>Account</Eyebrow>
      <h1 className="h1">Account</h1>
      <p className="lede">
        Signed in as <b className="c-ink">{user.email}</b>. We store only your name and email; everything else is the
        fleet data you entered.
      </p>

      <ReminderPreferences remind60={user.remind60} remind30={user.remind30} />
      <DeleteAccount email={user.email} />
    </div>
  );
}

/**
 * The two expiration notices, per user (§6).
 *
 * Opt-*out*, and both on by default: someone who entered an expiration date
 * wants to hear about it. Before this existed the only way to stop a reminder
 * was to delete the safety item it was about — trading away the tracking to
 * silence the mail, which is exactly backwards.
 *
 * Mail is live (Resend, docs/runbook.md §6), so the old "not sending yet"
 * warning is gone — leaving it up once delivery worked would be its own kind of
 * lie. The one-click unsubscribe link in every digest sets both toggles off; it
 * lands the person right back on these checkboxes to turn either back on.
 */
function ReminderPreferences({ remind60, remind30 }: { remind60: boolean; remind30: boolean }) {
  const update = useUpdatePreferences();

  return (
    <section className="card pref-card">
      <div className="card-h">Expiration reminders</div>

      <label className="pref-opt">
        <input
          type="checkbox"
          checked={remind60}
          disabled={update.isPending}
          onChange={(e) => update.mutate({ remind60: e.target.checked })}
        />
        <span>
          60-day notice
          <span>A first warning, two months out, while there is still time to order.</span>
        </span>
      </label>

      <label className="pref-opt">
        <input
          type="checkbox"
          checked={remind30}
          disabled={update.isPending}
          onChange={(e) => update.mutate({ remind30: e.target.checked })}
        />
        <span>
          30-day notice
          <span>The last call before the date passes and the gear is no longer legal.</span>
        </span>
      </label>

      <p className="pref-fine">
        Turning both off stops expiration email without touching your gear — the dates stay tracked and keep showing up
        on your timeline.
      </p>

      {update.isError && <div className="field-err">{errorMessage(update.error)}</div>}
    </section>
  );
}

/**
 * Delete the signed-in account, confirmed by typing its email.
 *
 * The button cannot be pressed until the typed string matches, so the API's
 * check is a backstop rather than the first line of defence. Both sides are
 * lowercased because the contract lowercases `confirmEmail`; a case-sensitive UI
 * check would block input the API would have accepted.
 *
 * There is no success branch to render: on success the mutation signs the user
 * out (which clears the whole cache), so this component unmounts with the rest
 * of the signed-in app.
 */
function DeleteAccount({ email }: { email: string }) {
  const [typed, setTyped] = useState('');
  const remove = useDeleteAccount();

  const matches = typed.trim().toLowerCase() === email.toLowerCase();

  return (
    <div className="danger-zone">
      <div className="danger-h">
        <Trash2 size={13} /> Delete your account
      </div>
      <p className="danger-p">
        <TriangleAlert size={13} /> This is permanent. It deletes your account and <b>every garage you own</b> — the
        cars, parts, events and service history in them. Garages you only crew for someone else are untouched.
      </p>
      <p className="danger-p danger-ask">
        Type <b>{email}</b> to confirm.
      </p>
      <input
        className="input"
        value={typed}
        autoFocus
        placeholder={email}
        onChange={(e) => setTyped(e.target.value)}
        aria-label="Type your email to confirm deletion"
      />

      {remove.isError && <div className="field-err">{errorMessage(remove.error)}</div>}

      <div className="danger-acts">
        <button
          className="act act-danger"
          disabled={!matches || remove.isPending}
          onClick={() => remove.mutate({ confirmEmail: email.toLowerCase() })}
        >
          {remove.isPending ? 'Deleting…' : 'Delete my account permanently'}
        </button>
      </div>
    </div>
  );
}
